import React, { useMemo, useState, useEffect } from "react";
import {
  Users, DollarSign, CalendarCheck, Star, TrendingUp, TrendingDown,
  ArrowUpRight, Mail, CreditCard, Landmark, Wallet, Bitcoin, Sparkles, Globe, Clock,
} from "lucide-react";
import { EXPERTS, PAYMENT_METHODS } from "../../mock/mock";
import { api } from "../../lib/api";

const loadExperts = () => JSON.parse(localStorage.getItem("intro_experts") || "null") || EXPERTS;

/* ---------- tiny SVG charts ---------- */
const Sparkline = ({ data, color = "#6366f1", w = 90, h = 30 }) => {
  const max = Math.max(...data), min = Math.min(...data);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / (max - min || 1)) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const AreaChart = ({ data, labels }) => {
  const W = 640, H = 200, pad = 8;
  const max = Math.max(...data) * 1.15 || 1;
  const step = (W - pad * 2) / (data.length - 1);
  const pt = (v, i) => [pad + i * step, H - pad - (v / max) * (H - pad * 2)];
  const line = data.map((v, i) => pt(v, i).join(",")).join(" ");
  const area = `${pad},${H - pad} ${line} ${W - pad},${H - pad}`;
  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-52">
        <defs>
          <linearGradient id="area-g" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line key={g} x1={pad} x2={W - pad} y1={(H - pad * 2) * g + pad} y2={(H - pad * 2) * g + pad} stroke="#f1f1f4" strokeWidth="1" />
        ))}
        <polygon points={area} fill="url(#area-g)" />
        <polyline points={line} fill="none" stroke="#6366f1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((v, i) => {
          const [x, y] = pt(v, i);
          return <circle key={i} cx={x} cy={y} r="4" fill="#fff" stroke="#6366f1" strokeWidth="2.5" />;
        })}
      </svg>
      <div className="flex justify-between px-2 mt-1">
        {labels.map((l) => <span key={l} className="text-xs text-neutral-400">{l}</span>)}
      </div>
    </div>
  );
};

const Donut = ({ segments, size = 150 }) => {
  const r = size / 2 - 12, c = 2 * Math.PI * r, cx = size / 2;
  let offset = 0;
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#f1f1f4" strokeWidth="14" />
      {segments.map((s, i) => {
        const len = (s.value / total) * c;
        const el = <circle key={i} cx={cx} cy={cx} r={r} fill="none" stroke={s.color} strokeWidth="14" strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} strokeLinecap="round" />;
        offset += len;
        return el;
      })}
    </svg>
  );
};

const StatCard = ({ icon: Icon, label, value, trend, up = true, tint, spark, sparkColor }) => (
  <div className="bg-white rounded-3xl p-5 border border-neutral-100 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${tint}`}><Icon className="w-5 h-5 text-white" /></div>
      {spark && <Sparkline data={spark} color={sparkColor} />}
    </div>
    <div className="mt-4 flex items-end justify-between">
      <div>
        <div className="font-display font-extrabold text-3xl text-neutral-900">{value}</div>
        <div className="text-sm text-neutral-500 mt-0.5">{label}</div>
      </div>
      {trend && (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${up ? "text-emerald-600 bg-emerald-50" : "text-rose-600 bg-rose-50"}`}>
          {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} {trend}
        </span>
      )}
    </div>
  </div>
);

const CAT_COLORS = { career: "#6366f1", decor: "#f59e0b", beauty: "#ec4899", wellness: "#10b981" };
const PAY_ICONS = { card: CreditCard, transfer: Landmark, paypal: Wallet, crypto: Bitcoin, demo: Sparkles };

const Overview = () => {
  const [experts, setExperts] = useState(loadExperts);
  const [bookings, setBookings] = useState([]);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.getExperts().then((r) => { if (r?.length) setExperts(r); }).catch(() => {});
    api.listBookings().then(setBookings).catch(() => {});
    api.listSubscribers().then(setSubs).catch(() => {});
  }, []);

  const revenueWeek = [3200, 4100, 3800, 5200, 4800, 6400, 5900];
  const bookingsWeek = [8, 12, 10, 15, 13, 18, 16];
  const days = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
  const totalRevenue = revenueWeek.reduce((a, b) => a + b, 0) + bookings.reduce((a, b) => a + (b.price || 0), 0);
  const totalBookings = bookingsWeek.reduce((a, b) => a + b, 0) + bookings.length;
  const avgPrice = Math.round(experts.reduce((a, e) => a + e.price, 0) / experts.length);

  const catSegments = ["career", "decor", "beauty", "wellness"].map((c) => ({
    key: c, value: experts.filter((e) => e.category === c).length, color: CAT_COLORS[c],
  }));

  const topExperts = [...experts].sort((a, b) => b.reviews - a.reviews).slice(0, 5);

  // payment breakdown (mock weighting)
  const payWeights = { card: 62, transfer: 21, paypal: 9, crypto: 3, demo: 5 };
  const langDist = [
    { l: "Türkçe", v: 48, c: "#6366f1" }, { l: "English", v: 31, c: "#10b981" },
    { l: "Русский", v: 14, c: "#f59e0b" }, { l: "العربية", v: 7, c: "#ec4899" },
  ];

  const activity = [
    ...bookings.slice(-4).reverse().map((b) => ({ icon: CalendarCheck, tint: "bg-indigo-100 text-indigo-600", text: `Yeni randevu → ${b.expert}`, sub: `${b.date || ""} ${b.time || ""}` })),
    { icon: Star, tint: "bg-amber-100 text-amber-600", text: "Yeni 5 yıldız değerlendirme", sub: "Alara Yıldız" },
    { icon: Mail, tint: "bg-rose-100 text-rose-600", text: "Yeni bülten aboneliği", sub: subs[subs.length - 1]?.email || "demo@intro.co" },
    { icon: Users, tint: "bg-emerald-100 text-emerald-600", text: "Yeni uzman başvurusu", sub: "Kariyer & İş" },
  ].slice(0, 6);

  return (
    <div className="space-y-6">
      {/* stat cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Toplam Gelir" value={`$${(totalRevenue / 1000).toFixed(1)}K`} trend="+18%" tint="bg-emerald-500" spark={revenueWeek} sparkColor="#10b981" />
        <StatCard icon={CalendarCheck} label="Randevular" value={totalBookings} trend="+12%" tint="bg-indigo-500" spark={bookingsWeek} sparkColor="#6366f1" />
        <StatCard icon={Users} label="Uzmanlar" value={experts.length} trend="+4%" tint="bg-violet-500" spark={[10,11,12,12,13,14,experts.length]} sparkColor="#8b5cf6" />
        <StatCard icon={Star} label="Ort. Puan" value="4.9" trend="+0.2" tint="bg-amber-500" spark={[4.6,4.7,4.7,4.8,4.8,4.9,4.9]} sparkColor="#f59e0b" />
      </div>

      {/* revenue + donut */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl p-6 border border-neutral-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-lg text-neutral-900">Gelir Grafiği</h3>
              <p className="text-sm text-neutral-400">Son 7 gün</p>
            </div>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full"><ArrowUpRight className="w-4 h-4" /> +18.2%</span>
          </div>
          <AreaChart data={revenueWeek} labels={days} />
        </div>
        <div className="bg-white rounded-3xl p-6 border border-neutral-100 shadow-sm">
          <h3 className="font-display font-bold text-lg text-neutral-900 mb-4">Kategori Dağılımı</h3>
          <div className="flex flex-col items-center">
            <div className="relative">
              <Donut segments={catSegments} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display font-extrabold text-2xl text-neutral-900">{experts.length}</span>
                <span className="text-xs text-neutral-400">Uzman</span>
              </div>
            </div>
            <div className="mt-4 w-full space-y-2">
              {catSegments.map((s) => (
                <div key={s.key} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-neutral-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} /> <span className="capitalize">{s.key}</span></span>
                  <span className="font-semibold text-neutral-900">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* bookings bar + payment methods */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl p-6 border border-neutral-100 shadow-sm">
          <h3 className="font-display font-bold text-lg text-neutral-900 mb-5">Haftalık Randevular</h3>
          <div className="flex items-end justify-between gap-3" style={{ height: "180px" }}>
            {bookingsWeek.map((v, i) => {
              const max = Math.max(...bookingsWeek);
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full gap-2">
                  <span className="text-xs font-semibold text-neutral-500">{v}</span>
                  <div className="w-full rounded-t-lg bg-gradient-to-t from-indigo-600 to-violet-400 hover:opacity-80 transition-all" style={{ height: `${(v / max) * 130}px` }} />
                  <span className="text-xs text-neutral-400">{days[i]}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-white rounded-3xl p-6 border border-neutral-100 shadow-sm">
          <h3 className="font-display font-bold text-lg text-neutral-900 mb-4">Ödeme Yöntemleri</h3>
          <div className="space-y-3.5">
            {PAYMENT_METHODS.map((m) => {
              const Icon = PAY_ICONS[m.id]; const pct = payWeights[m.id] || 0;
              return (
                <div key={m.id}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="flex items-center gap-2 text-neutral-600"><Icon className="w-4 h-4 text-neutral-400" /> {m.label.tr}</span>
                    <span className="font-semibold text-neutral-900">%{pct}</span>
                  </div>
                  <div className="h-2 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* top experts + activity + languages */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden">
          <div className="p-6 pb-3"><h3 className="font-display font-bold text-lg text-neutral-900">En İyi Uzmanlar</h3></div>
          <div className="divide-y divide-neutral-100">
            {topExperts.map((e, i) => (
              <div key={e.id} className="flex items-center gap-4 px-6 py-3 hover:bg-neutral-50">
                <span className="w-6 text-center font-display font-bold text-neutral-300">{i + 1}</span>
                <img src={e.photo} alt={e.name} className="w-10 h-10 rounded-xl object-cover" />
                <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-neutral-900 truncate">{e.name}</div><div className="text-xs text-neutral-400 flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{e.rating} · {e.reviews} değerlendirme</div></div>
                <div className="font-display font-bold text-neutral-900">${e.price}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 border border-neutral-100 shadow-sm">
            <h3 className="font-display font-bold text-lg text-neutral-900 mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-neutral-400" /> Dil Dağılımı</h3>
            <div className="space-y-3">
              {langDist.map((x) => (
                <div key={x.l}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-neutral-600">{x.l}</span><span className="font-semibold text-neutral-900">%{x.v}</span></div>
                  <div className="h-2 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${x.v}%`, background: x.c }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* activity feed */}
      <div className="bg-white rounded-3xl p-6 border border-neutral-100 shadow-sm">
        <h3 className="font-display font-bold text-lg text-neutral-900 mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-neutral-400" /> Son Hareketler</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {activity.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={i} className="flex items-center gap-3 rounded-2xl border border-neutral-100 p-3">
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${a.tint}`}><Icon className="w-4 h-4" /></span>
                <div className="min-w-0"><div className="text-sm font-medium text-neutral-800 truncate">{a.text}</div><div className="text-xs text-neutral-400 truncate">{a.sub}</div></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Overview;
